/**
 * Redux Slice for Wallet Management
 * Manages the state of cryptocurrency wallet connection, including connection status,
 * address, chain information, and balance
 */

import { createSlice } from "@reduxjs/toolkit";

/**
 * Initial state for the wallet slice
 * @type {Object}
 * @property {string|null} address - The connected wallet address
 * @property {string|null} chainId - The current blockchain network ID
 * @property {boolean} isConnected - Whether the wallet is currently connected
 * @property {boolean} isConnecting - Whether a connection attempt is in progress
 * @property {string|null} error - Any error message from wallet operations
 * @property {string|null} balance - The wallet's current balance
 */
const initialState = {
  address: null,
  chainId: null,
  isConnected: false,
  isConnecting: false,
  error: null,
  balance: null,
  balanceError: null,
};

/**
 * Redux slice for wallet functionality
 * Provides actions and reducers for wallet connection management
 */
const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    /**
     * Initiates wallet connection process
     * @param {Object} state - Current Redux state
     */
    connectWalletRequest: (state) => {
      state.isConnecting = true;
      state.error = null;
    },

    /**
     * Handles successful wallet connection
     * @param {Object} state - Current Redux state
     * @param {Object} action - Redux action object
     * @param {Object} action.payload - Connection payload
     * @param {string} action.payload.address - Connected wallet address
     * @param {string} action.payload.chainId - Connected chain ID
     */
    connectWalletSuccess: (state, action) => {
      state.isConnecting = false;
      state.error = null;
      state.balance = null;
      state.balanceError = null;
      state.isConnected = true;
      state.address = action.payload.address;
      state.chainId = action.payload.chainId;
    },

    /**
     * Handles wallet connection failures
     * @param {Object} state - Current Redux state
     * @param {Object} action - Redux action object
     * @param {string} action.payload - Error message
     */
    connectWalletFailure: (state, action) => {
      return { ...initialState, error: action.payload };
    },

    /**
     * Disconnects the wallet and resets state
     * @param {Object} state - Current Redux state
     * @returns {Object} Initial state
     */
    disconnectWallet: () => {
      return initialState;
    },

    /**
     * Updates the wallet balance
     * @param {Object} state - Current Redux state
     * @param {Object} action - Redux action object
     * @param {string} action.payload - New balance value
     */
    updateBalance: (state, action) => {
      state.balance = action.payload;
      state.balanceError = null;
    },

    updateBalanceError: (state, action) => {
      state.balance = null;
      state.balanceError = action.payload;
    },

    /**
     * Updates chain ID after successful network switch
     * @param {Object} state - Current Redux state
     * @param {Object} action - Redux action object
     * @param {string} action.payload - New chain ID
     */
    switchChainSuccess: (state, action) => {
      state.chainId = action.payload;
    },
  },
});

/**
 * Exported wallet actions for dispatch
 */
export const {
  connectWalletRequest,
  connectWalletSuccess,
  connectWalletFailure,
  disconnectWallet,
  updateBalance,
  updateBalanceError,
  switchChainSuccess,
} = walletSlice.actions;

export default walletSlice.reducer;
